/**
 * Centralized formatting utility for UI components
 * Used by Kpi, StatList, and other data display components
 */

import React from "react";
import { formatFieldValue } from "./index";
import type { FieldFormat, FieldValue } from "./types";

/**
 * Common formatter type used by data display components
 * Can be either a custom function or a FieldFormat object
 */
export type ComponentFormatter = ((value: FieldValue) => React.ReactNode) | FieldFormat;

/**
 * Options for formatting values in components
 */
export interface ComponentFormatOptions {
  /** The value to format */
  value: FieldValue;
  /** The formatter to apply */
  formatter?: ComponentFormatter;
  /** CSS class for null/empty values */
  emptyClassName?: string;
  /** Text to show for null/empty values */
  emptyText?: React.ReactNode;
}

/**
 * Format a value for display in a component
 * Centralizes the logic used by Kpi, StatList, and similar components
 *
 * @example
 * ```tsx
 * // In a component
 * const formatted = formatComponentValue({
 *   value: 1500,
 *   formatter: { type: "power" }
 * });
 * // Returns: "1.5 kW"
 * ```
 */
export function formatComponentValue({
  value,
  formatter,
  emptyClassName = "text-text-secondary",
  emptyText = "—",
}: ComponentFormatOptions): React.ReactNode {
  // Handle null/undefined early
  if (value == null) {
    return <span className={emptyClassName}>{emptyText}</span>;
  }

  // Custom formatter function takes precedence
  if (typeof formatter === "function") {
    return formatter(value);
  }

  // If a FieldFormat is provided, use the unified formatting system
  if (formatter && typeof formatter === "object") {
    const formatted = formatFieldValue(value, formatter);

    // Wrap dates in semantic time element for accessibility
    if (value instanceof Date && formatted) {
      return (
        <time dateTime={value.toISOString()} title={value.toISOString()}>
          {formatted}
        </time>
      );
    }

    return formatted || <span className={emptyClassName}>{emptyText}</span>;
  }

  // No formatter provided - use sensible defaults based on type
  if (!formatter) {
    // For strings, just return as-is
    if (typeof value === "string") {
      return value;
    }

    // For dates, use default date formatting
    if (value instanceof Date) {
      const formatted = formatFieldValue(value, { type: "date" });
      return (
        <time dateTime={value.toISOString()} title={value.toISOString()}>
          {formatted}
        </time>
      );
    }

    // For numbers, use default number formatting
    if (typeof value === "number") {
      return formatFieldValue(value, { type: "number" });
    }

    // For booleans, use default boolean formatting
    if (typeof value === "boolean") {
      return formatFieldValue(value, { type: "boolean" });
    }
  }

  // Fallback to string conversion
  return String(value);
}

/**
 * Hook for using the component formatter
 * Provides memoization for better performance
 *
 * @example
 * ```tsx
 * function MyComponent({ value, formatter }) {
 *   const format = useComponentFormatter(formatter);
 *   const formatted = format(value);
 *   return <span>{formatted}</span>;
 * }
 * ```
 */
export function useComponentFormatter(
  formatter?: ComponentFormatter,
  emptyClassName?: string,
  emptyText?: React.ReactNode
) {
  return React.useCallback(
    (value: FieldValue) =>
      formatComponentValue({
        value,
        formatter,
        emptyClassName,
        emptyText,
      }),
    [formatter, emptyClassName, emptyText]
  );
}
